import React, { useReducer } from "react";
import TripContext from "./tripContext";
import tripReducer from "./tripReducer";
import { API_BASE, apiErrorMessage, authedFetch } from "../../utils/api";

import {
  ADD_TRIP,
  ADD_TRIP_COMPLETE,
  ADD_TRIP_ERROR,
  UPDATE_TRIP,
  UPDATE_TRIP_ERROR,
  DELETE_TRIP,
  DELETE_TRIP_ERROR,
  ATTACH_TRIP_REVIEW_ERROR,
  DETACH_TRIP_REVIEW,
  DETACH_TRIP_REVIEW_ERROR,
  GET_TRIP_DETAIL,
  GET_TRIP_DETAIL_ERROR,
  GET_TRIPS,
  GET_TRIPS_ERROR,
  GET_USER_TRIPS,
  GET_USER_TRIPS_ERROR,
} from "../types";


const TripState = (props) => {
  const initialState = {
    tripDetail: null,
    trips: [],
    tripsTotal: 0,
    tripsPage: 1,
    tripsPageSize: 3,
    tripsHasMore: false,
    userTrips: [],
    userTripsTotal: 0,
    userTripsPage: 1,
    userTripsPageSize: 3,
    userTripsHasMore: false,
    loading: true,
    tripAdded: false,
    message: null,
  };

  const [state, dispatch] = useReducer(tripReducer, initialState);

  const getTrips = async (page = 1, pageSize = 3, append = false) => {
    const url = `${API_BASE}/trips?page=${page}&page_size=${pageSize}`;

    const res = await authedFetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });
    const res_json = await res.json();
    if (res.ok) {
      dispatch({
        type: GET_TRIPS,
        payload: {
          items: res_json.items || [],
          total: res_json.total || 0,
          page: res_json.page || page,
          pageSize: res_json.page_size || pageSize,
          hasMore: !!res_json.has_more,
          append,
        },
      });
    } else {
      const error_msg = res.status === 401 ? "Unauthorized" : apiErrorMessage(res_json);
      dispatch({ type: GET_TRIPS_ERROR, payload: error_msg });
    }
  }

  const getUserTrips = async (page = 1, pageSize = 3, append = false) => {
    const url = `${API_BASE}/user/trips?page=${page}&page_size=${pageSize}`;
    const res = await authedFetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });
    const res_json = await res.json();
    if (res.ok) {
      dispatch({
        type: GET_USER_TRIPS,
        payload: {
          items: res_json.items || [],
          total: res_json.total || 0,
          page: res_json.page || page,
          pageSize: res_json.page_size || pageSize,
          hasMore: !!res_json.has_more,
          append,
        },
      });
    } else {
      const error_msg = res.status === 401 ? "Unauthorized" : apiErrorMessage(res_json);
      dispatch({ type: GET_USER_TRIPS_ERROR, payload: error_msg });
    }
  }

  const addTrip = async (trip) => {
    const formData = new FormData();
    formData.append("title", trip.title);
    formData.append("content", trip.content);
    if (trip.destinationID) formData.append("destination_id", trip.destinationID);
    if (trip.visibility) formData.append("visibility", trip.visibility);
    // Serialize a single draft review (parent or sub) into the JSON shape
    // the backend expects. Photos are intentionally NOT appended here — the
    // caller knows the file-key prefix (photo-i-j for parents, sub-photo-i-k-j
    // for subs) and handles that after the record is built.
    const serializeReviewFields = (review) => {
      const photoFiles = review.files && review.files.length > 0
        ? review.files
        : (review.photos || []);
      const data = {
        title: review.title,
        content: review.content,
        place_id: review.placeId,
        photo_num: photoFiles.length,
      };
      if (review.price !== undefined && review.price !== null && review.price !== "") {
        data.price = Number(review.price);
      }
      if (review.placeTypes && review.placeTypes.length > 0) {
        data.place_types = review.placeTypes;
      }
      if (review.category) {
        data.category = review.category;
      }
      if (review.tripOnly) {
        data.trip_only = true;
      }
      if (review.visitStartTime) {
        const startMs = new Date(review.visitStartTime).getTime();
        data.visit_from = Math.floor(startMs / 1000);
        if (review.visitDuration) {
          const endMs = startMs + parseInt(review.visitDuration) * 60 * 1000;
          data.visit_to = Math.floor(endMs / 1000);
        }
      }
      return { data, photoFiles };
    };

    const spotReviewArr = [];
    for (let i = 0; i < trip.spotReviews.length; i++) {
      const review = trip.spotReviews[i];
      // Existing review: link by id, no photo upload, backend ignores other fields.
      if (review.existingReviewId) {
        spotReviewArr.push({
          existing_review_id: review.existingReviewId,
          photo_num: 0,
          order: i,
        });
        continue;
      }
      const { data: reviewData, photoFiles } = serializeReviewFields(review);
      reviewData.order = i;

      // Sub-reviews attached to a draft parent. Each sub gets the same
      // fields (no order/existing_review_id) plus a relation label. The
      // backend will link them to the just-created parent.
      const subDrafts = review.subReviews || [];
      if (subDrafts.length > 0) {
        const subArr = [];
        for (let k = 0; k < subDrafts.length; k++) {
          const sub = subDrafts[k];
          const { data: subData, photoFiles: subPhotos } = serializeReviewFields(sub);
          subData.relation = sub.relation;
          subArr.push(subData);
          for (let j = 0; j < subPhotos.length; j++) {
            formData.append(`sub-photo-${i}-${k}-${j}`, subPhotos[j]);
          }
        }
        reviewData.sub_reviews = subArr;
      }

      spotReviewArr.push(reviewData);
      for (let j = 0; j < photoFiles.length; j++) {
        formData.append(`photo-${i}-${j}`, photoFiles[j]);
      }
    }
    formData.append("spot_reviews", JSON.stringify(spotReviewArr))
    // formData.append("spot_reviews", trip.spotReviewIDs);

    const res = await authedFetch(`${API_BASE}/trip`, {
      method: "POST",
      body: formData,
    });
    const res_json = await res.json();
    if (res.ok) {
      dispatch({ type: ADD_TRIP, payload: res_json });
    } else {
      dispatch({ type: ADD_TRIP_ERROR, payload: apiErrorMessage(res_json) });
    }
  };

  const getTripForID = async (trip_id) => {
    console.log(trip_id);
    var url = `${API_BASE}/trip/` + trip_id;
    const res = await authedFetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });
    const res_json = await res.json();
    if (res.ok) {
      dispatch({ type: GET_TRIP_DETAIL, payload: res_json });
    } else {
      var error_msg = null;
      if (res.status === 401) {
        error_msg = "Unauthorized";
      } else {
        error_msg = apiErrorMessage(res_json);
      }
      dispatch({ type: GET_TRIP_DETAIL_ERROR, payload: error_msg });
    }
  };

  const getTripsForDestination = async (destination_id) => {
    const url = `${API_BASE}/destination/` + destination_id + "/trips";
    const res = await authedFetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });
    const res_json = await res.json();
    if (res.ok) {
      // This endpoint returns a plain array (not paginated). Wrap it so the
      // reducer treats it as a full replacement of the trips list.
      const items = Array.isArray(res_json) ? res_json : [];
      dispatch({
        type: GET_TRIPS,
        payload: {
          items,
          total: items.length,
          page: 1,
          pageSize: items.length,
          hasMore: false,
          append: false,
        },
      });
    } else {
      const error_msg = res.status === 401 ? "Unauthorized" : apiErrorMessage(res_json);
      dispatch({ type: GET_TRIPS_ERROR, payload: error_msg });
    }
  };

  const updateTrip = async (trip_id, fields) => {
    const formData = new FormData();
    if (fields.title != null) formData.append("title", fields.title);
    if (fields.content != null) formData.append("content", fields.content);

    const res = await authedFetch(`${API_BASE}/trip/${trip_id}`, {
      method: "PUT",
      body: formData,
    });
    const res_json = await res.json();
    if (res.ok) {
      dispatch({ type: UPDATE_TRIP, payload: res_json });
      return { ok: true };
    } else {
      dispatch({ type: UPDATE_TRIP_ERROR, payload: apiErrorMessage(res_json) });
      return { ok: false, msg: apiErrorMessage(res_json) };
    }
  };

  const deleteTrip = async (trip_id) => {
    const res = await authedFetch(`${API_BASE}/trip/` + trip_id, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
      },
    });
    if (res.ok) {
      dispatch({ type: DELETE_TRIP, payload: trip_id });
    } else {
      let error_msg = "Failed to delete trip";
      try {
        const res_json = await res.json();
        error_msg = res.status === 401 ? "Unauthorized" : apiErrorMessage(res_json) || error_msg;
      } catch (e) {}
      dispatch({ type: DELETE_TRIP_ERROR, payload: error_msg });
    }
  };

  const attachTripReview = async (trip_id, review_id) => {
    const formData = new FormData();
    formData.append("review_id", review_id);
    const res = await authedFetch(`${API_BASE}/trip/${trip_id}/review`, {
      method: "POST",
      body: formData,
    });
    if (res.ok) {
      // Refetch the trip to get the updated spot_reviews list.
      await getTripForID(trip_id);
      return { ok: true };
    }
    let error_msg = "Failed to add review to trip";
    try {
      const res_json = await res.json();
      error_msg = apiErrorMessage(res_json) || error_msg;
    } catch (e) {}
    dispatch({ type: ATTACH_TRIP_REVIEW_ERROR, payload: error_msg });
    return { ok: false, msg: error_msg };
  };

  const detachTripReview = async (trip_id, review_id) => {
    const res = await authedFetch(`${API_BASE}/trip/${trip_id}/review/${review_id}`, {
      method: "DELETE",
    });
    if (res.ok) {
      dispatch({ type: DETACH_TRIP_REVIEW, payload: { trip_id, review_id } });
      return { ok: true };
    }
    let error_msg = "Failed to remove review from trip";
    try {
      const res_json = await res.json();
      error_msg = apiErrorMessage(res_json) || error_msg;
    } catch (e) {}
    dispatch({ type: DETACH_TRIP_REVIEW_ERROR, payload: error_msg });
    return { ok: false, msg: error_msg };
  };

  const addTripComplete = () => {
    dispatch({ type: ADD_TRIP_COMPLETE });
  };

  const publishTrip = async (tripId) => {
    const res = await authedFetch(`${API_BASE}/trip/${tripId}/publish`, {
      method: "PUT",
    });
    const res_json = await res.json();
    if (res.ok) {
      dispatch({ type: GET_TRIP_DETAIL, payload: res_json });
      return { ok: true, data: res_json };
    } else {
      return { ok: false, msg: apiErrorMessage(res_json) };
    }
  };

  return (
    <TripContext.Provider
      value={{
        trips: state.trips,
        tripsTotal: state.tripsTotal,
        tripsPage: state.tripsPage,
        tripsPageSize: state.tripsPageSize,
        tripsHasMore: state.tripsHasMore,
        userTrips: state.userTrips,
        userTripsTotal: state.userTripsTotal,
        userTripsPage: state.userTripsPage,
        userTripsPageSize: state.userTripsPageSize,
        userTripsHasMore: state.userTripsHasMore,
        loading: state.loading,
        tripAdded: state.tripAdded,
        message: state.message,
        tripDetail: state.tripDetail,
        getTrips,
        getUserTrips,
        getTripsForDestination,
        addTrip,
        addTripComplete,
        updateTrip,
        attachTripReview,
        detachTripReview,
        deleteTrip,
        getTripForID,
        publishTrip,
      }}
    >
      {props.children}
    </TripContext.Provider>
  );
};

export default TripState;
